import { type EventProperties } from '@segment/analytics-next';
import { useLocation } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useAnalyticsContext } from '@/components/Analytics/useAnalyticsContext';
import { HUBSPOT_UTK_COOKIE, HUBSPOT_UTK_TLD_COOKIE } from '@/utils/analytics-utils';
import { Cookies } from '@/utils/cookies';
import { useReferrer } from './use-referrer';

let gaSessionId: string | undefined;
let gaClientId: string | undefined;

// Google Analytics Cookies
function getGAProperties() {
  if (!(gaSessionId && gaClientId)) {
    const gaMeasurementId = window.__QDRANT_CLOUD__.ga_measurement_id.replace('G-', '');
    gaSessionId = Cookies.get(`_ga_${gaMeasurementId}`)?.replace('GS1.1.', '').split('.')[0];
    gaClientId = Cookies.get('_ga')?.replace('GA1.1.', '');
  }
  return { ga_session_id: gaSessionId, ga_client_id: gaClientId };
}

type EventPayload = EventProperties;

type PayloadWithTimestamp = {
  timestamp?: Date;
} & EventPayload;

/**
 * Queue event calls until cookie consent is granted, then dispatch them all
 */
function useTrackEventQueue<T extends (...args: any[]) => void>(trackEvent: T) {
  const { enqueueEvent } = useAnalyticsContext();

  return useCallback(
    (eventPayload: EventPayload = {}) => {
      enqueueEvent((timestamp) => trackEvent({ ...eventPayload, timestamp }));
    },
    [enqueueEvent, trackEvent],
  ) as T;
}

/**
 * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
 * @returns A hook that tracks page views
 */
export function useTrackPageView() {
  const { href } = useLocation();
  const referrer = useReferrer();

  return useTrackEventQueue(
    useCallback(
      (actionPayload: EventPayload = {}) => {
        const { timestamp, ...eventPayload } = actionPayload as PayloadWithTimestamp;
        void window.analytics.page(
          document.title,
          {
            url: href,
            referrer,
            ...eventPayload,
            ...getGAProperties(),
            [`qdrant_tech_${HUBSPOT_UTK_COOKIE}`]: Cookies.get(HUBSPOT_UTK_TLD_COOKIE),
          },
          { timestamp },
        );
      },
      [href, referrer],
    ),
  );
}
